import React, { useEffect, useId, useRef, useState } from 'react';
import './KioskSelect.css';

/**
 * Compact, themed replacement for a native <select>.
 *
 * Native dropdowns open as a large, touch-sized list on the kiosk's Chromium and
 * can't be resized with CSS there, so this draws its own list instead.
 *
 * Drop-in usage: same `name`, `value`, `onChange` and <option> children as a
 * <select>. `onChange` receives `{ target: { name, value } }`, matching what
 * handlers read from a native change event.
 */
function KioskSelect({ name, value, onChange, children, className = '', disabled = false }) {
    const options = React.Children.toArray(children)
        .filter((child) => React.isValidElement(child))
        .map((child) => ({ value: String(child.props.value), label: child.props.children }));

    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const rootRef = useRef(null);
    const listId = useId();

    const selectedIndex = options.findIndex((option) => option.value === String(value));
    const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : '';

    // Close when tapping anywhere outside the dropdown
    useEffect(() => {
        if (!open) return undefined;
        const handlePointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [open]);

    const openList = () => {
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        setOpen(true);
    };

    const choose = (index) => {
        const option = options[index];
        if (!option) return;
        setOpen(false);
        if (option.value !== String(value)) {
            onChange?.({ target: { name, value: option.value } });
        }
    };

    const handleKeyDown = (event) => {
        if (disabled) return;
        if (!open) {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
                event.preventDefault();
                openList();
            }
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => Math.min(options.length - 1, index + 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => Math.max(0, index - 1));
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            choose(activeIndex);
        }
    };

    return (
        <div ref={rootRef} className={`kiosk-select${open ? ' kiosk-select--open' : ''} ${className}`.trim()}>
            <button
                type="button"
                className="kiosk-select__trigger"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listId}
                disabled={disabled}
                onClick={() => (open ? setOpen(false) : openList())}
                onKeyDown={handleKeyDown}
            >
                <span className="kiosk-select__value">{selectedLabel}</span>
                <span className="kiosk-select__chevron" aria-hidden="true" />
            </button>
            {open && (
                <ul id={listId} className="kiosk-select__list" role="listbox">
                    {options.map((option, index) => (
                        <li
                            key={option.value}
                            role="option"
                            aria-selected={index === selectedIndex}
                            className={`kiosk-select__option${index === selectedIndex ? ' is-selected' : ''}${index === activeIndex ? ' is-active' : ''}`}
                            onPointerEnter={() => setActiveIndex(index)}
                            onClick={(event) => {
                                // Stops a wrapping <label> from re-clicking the trigger and reopening the list
                                event.preventDefault();
                                choose(index);
                            }}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default KioskSelect;
